import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { uploadFile } from '../../utils/api';
import toast from 'react-hot-toast';
import { RELEASE_TYPES, LANGUAGES, CONTRIBUTOR_ROLES, ARTIST_ROLES, WORK_TYPES, makeDefaultContributors, makeDefaultArtists } from '../../utils/constants';
import { Music, Upload, Plus, Trash2, GripVertical, Save, Send, ArrowLeft, Image, Check, X, ChevronDown, ChevronUp, UserPlus, Globe, Instagram, Facebook, Youtube, Music2 } from 'lucide-react';

// ─── Modal completo artista (identico al frontend utente, tema dark, endpoint admin) ──
function AdminArtistModal({ userId, onCreated, onClose }) {
  const [form, setForm] = useState({ name: '', spotify_uri: '', apple_music_id: '', photo_path: '', biography: '',
    link_instagram: '', link_facebook: '', link_youtube: '', link_tiktok: '', link_website: '' });
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const { data } = await uploadFile(`/uploads/artwork?for_user=${userId}`, f, 'artwork');
      setForm(p => ({ ...p, photo_path: data.file.filename }));
      setPhotoPreview(URL.createObjectURL(f));
      toast.success('Foto caricata');
    } catch (err) { toast.error('Errore upload'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Nome obbligatorio');
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/artists`, form);
      toast.success(`Artista "${data.artist.name}" creato!`);
      onCreated(data.artist);
      onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Errore'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] px-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-lg">Nuovo Artista</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white"><X size={18}/></button>
        </div>
        <p className="text-xs text-yellow-400 mb-3">Verrà creato nel profilo dell'utente selezionato.</p>
        <div className="space-y-4">
          {/* Foto */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gray-700 border-2 border-dashed border-gray-600 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {uploading ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-400"/>
                : photoPreview ? <img src={photoPreview} alt="" className="w-full h-full object-cover"/>
                : <Image size={24} className="text-gray-500"/>}
            </div>
            <div>
              <input type="file" accept="image/*" className="hidden" id="adm-cr-artist-photo" onChange={handlePhoto}/>
              <label htmlFor="adm-cr-artist-photo" className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg cursor-pointer hover:bg-gray-600 inline-flex items-center gap-1.5 text-xs">
                <Upload size={12}/>Foto Artista
              </label>
              <p className="text-[10px] text-gray-500 mt-1">JPG/PNG, quadrata, 500×500px</p>
              {photoPreview && <button onClick={() => { setForm(p => ({ ...p, photo_path: '' })); setPhotoPreview(null); }} className="text-[10px] text-red-400 hover:underline mt-1">Rimuovi</button>}
            </div>
          </div>
          <div><label className="block text-sm font-medium text-gray-300 mb-1">Nome Artista *</label>
            <input className="input-field-dark w-full" autoFocus value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} onKeyDown={e => e.key === 'Enter' && save()} placeholder="Nome artista..."/></div>
          <div><label className="block text-sm font-medium text-gray-300 mb-1">Biografia</label>
            <textarea className="input-field-dark w-full h-20 resize-y text-xs" value={form.biography} onChange={e => setForm(p => ({ ...p, biography: e.target.value }))} placeholder="Breve biografia..."/></div>
          {/* Piattaforme */}
          <div className="border-t border-gray-700 pt-3">
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Piattaforme Musicali</p>
            <div className="space-y-2">
              <div><label className="block text-[10px] text-gray-500 mb-1">Spotify URI</label><input className="input-field-dark w-full" value={form.spotify_uri} onChange={e => setForm(p => ({ ...p, spotify_uri: e.target.value }))} placeholder="spotify:artist:..."/></div>
              <div><label className="block text-[10px] text-gray-500 mb-1">Apple Music ID</label><input className="input-field-dark w-full" value={form.apple_music_id} onChange={e => setForm(p => ({ ...p, apple_music_id: e.target.value }))} placeholder="https://music.apple.com/artist/..."/></div>
            </div>
          </div>
          {/* Social */}
          <div className="border-t border-gray-700 pt-3">
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Link & Social</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2"><Globe size={14} className="text-gray-500 flex-shrink-0"/><input className="input-field-dark" value={form.link_website} onChange={e => setForm(p => ({ ...p, link_website: e.target.value }))} placeholder="https://..."/></div>
              <div className="flex items-center gap-2"><Instagram size={14} className="text-gray-500 flex-shrink-0"/><input className="input-field-dark" value={form.link_instagram} onChange={e => setForm(p => ({ ...p, link_instagram: e.target.value }))} placeholder="https://instagram.com/..."/></div>
              <div className="flex items-center gap-2"><Facebook size={14} className="text-gray-500 flex-shrink-0"/><input className="input-field-dark" value={form.link_facebook} onChange={e => setForm(p => ({ ...p, link_facebook: e.target.value }))} placeholder="https://facebook.com/..."/></div>
              <div className="flex items-center gap-2"><Youtube size={14} className="text-gray-500 flex-shrink-0"/><input className="input-field-dark" value={form.link_youtube} onChange={e => setForm(p => ({ ...p, link_youtube: e.target.value }))} placeholder="https://youtube.com/@..."/></div>
              <div className="flex items-center gap-2"><Music2 size={14} className="text-gray-500 flex-shrink-0"/><input className="input-field-dark" value={form.link_tiktok} onChange={e => setForm(p => ({ ...p, link_tiktok: e.target.value }))} placeholder="https://tiktok.com/@..."/></div>
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600">Annulla</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-yellow-500 text-black rounded-lg text-sm font-bold hover:bg-yellow-400 disabled:opacity-50">
            {saving ? 'Salvataggio...' : 'Crea Artista'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Riga artista con autocomplete ────────────────────────────────────────────
function ArtistRowCreate({ idx, artist, userArtists, onChange, onRemove, canRemove, onCreateNew }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const query = (artist.name || '').toLowerCase();
  const filtered = query.length >= 1
    ? userArtists.filter(a => a.name.toLowerCase().includes(query))
    : userArtists;

  return (
    <div className="flex items-center gap-2 mb-2">
      <select className="input-field-dark w-32 text-xs" value={artist.role}
        onChange={e => onChange({ ...artist, role: e.target.value })}>
        {ARTIST_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
      <div className="flex-1 relative">
        <input
          className={`input-field-dark w-full ${artist.artist_id ? 'border-green-700 bg-green-900/10' : ''}`}
          placeholder="Nome artista..."
          value={artist.name || ''}
          onChange={e => { onChange({ ...artist, name: e.target.value, artist_id: null }); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        />
        {artist.artist_id && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-green-400 font-bold pointer-events-none">✓</span>
        )}
        {showSuggestions && (
          <div className="absolute z-40 top-full left-0 right-0 bg-gray-700 border border-gray-600 rounded-lg shadow-xl mt-0.5 max-h-40 overflow-y-auto">
            {filtered.map(a => (
              <button key={a.id}
                onMouseDown={() => { onChange({ ...artist, name: a.name, artist_id: a.id }); setShowSuggestions(false); }}
                className="w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-gray-600 flex items-center gap-2 border-b border-gray-600/50 last:border-0">
                {a.photo_path
                  ? <img src={`/uploads/artwork/${a.user_id}/${a.photo_path}`} className="w-5 h-5 rounded-full object-cover flex-shrink-0" alt=""/>
                  : <div className="w-5 h-5 rounded-full bg-yellow-800 text-yellow-300 text-[9px] flex items-center justify-center flex-shrink-0 font-bold">{a.name?.[0]}</div>}
                <span className="flex-1 truncate">{a.name}</span>
                {a.spotify_uri && <span className="text-[9px] text-green-400">S</span>}
                {a.apple_music_id && <span className="text-[9px] text-pink-400">A</span>}
              </button>
            ))}
            <button onMouseDown={onCreateNew}
              className="w-full text-left px-3 py-2 text-[10px] text-yellow-400 hover:bg-gray-600 flex items-center gap-1.5 border-t border-gray-600">
              <UserPlus size={10}/> {artist.name ? `Crea "${artist.name}" come nuovo artista` : 'Crea nuovo artista...'}
            </button>
          </div>
        )}
      </div>
      {canRemove && (
        <button onClick={onRemove} className="p-1 text-gray-500 hover:text-red-500 flex-shrink-0"><X size={14}/></button>
      )}
    </div>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function AdminReleaseCreatePage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [labels, setLabels] = useState([]);
  const [genres, setGenres] = useState([]);
  const [userArtists, setUserArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [releaseId, setReleaseId] = useState(null);
  const [artistModal, setArtistModal] = useState({ open: false, trackIdx: null, artistIdx: null });
  const [release, setRelease] = useState({
    title: '', release_type: 'single', label_id: '', cover_art_path: '',
    release_date: '', copyright_phonographic: '', copyright_phonographic_year: '',
    upc: '', content_id_youtube: false, previously_distributed: false, original_release_date: ''
  });
  const [releaseArtists, setReleaseArtists] = useState([{ name: '', role: 'primary', artist_id: null }]);
  const [tracks, setTracks] = useState([makeTrack(1)]);
  const [coverPreview, setCoverPreview] = useState(null);

  function makeTrack(n) {
    return {
      _id: Date.now() + Math.random(), track_number: n, title: '', version_label: '',
      work_type: '', audio_path: '', audio_format: '', audio_duration_seconds: 0,
      audio_sample_rate: 0, audio_bit_depth: 0, language: '', genre1_id: '', genre2_id: '',
      explicit_content: null, isrc: '', lyrics: '', copyright_text: '', copyright_year: '',
      preview_start_seconds: 0, ai_generated: false, instrumental: false,
      artists: makeDefaultArtists(), contributors: makeDefaultContributors(),
      _expanded: true, _uploading: false, _progress: 0
    };
  }

  useEffect(() => {
    (async () => {
      try {
        const [uRes, gRes] = await Promise.all([
          api.get('/admin/users?limit=500'),
          api.get('/genres').catch(() => ({ data: { genres: [] } }))
        ]);
        setUsers((uRes.data.users || []).filter(u => u.role !== 'admin'));
        setGenres(gRes.data.genres || []);
      } catch (e) {}
      finally { setLoading(false); }
    })();
  }, []);

  // Carica label e artisti quando cambia l'utente selezionato
  useEffect(() => {
    if (!selectedUser) { setLabels([]); setUserArtists([]); return; }
    (async () => {
      try {
        const [lRes, aRes] = await Promise.all([
          api.get(`/labels/admin/by-user/${selectedUser}`),
          api.get(`/admin/users/${selectedUser}/artists`)
        ]);
        setLabels(lRes.data.labels || []);
        setUserArtists(aRes.data.artists || []);
      } catch (e) { setLabels([]); setUserArtists([]); }
    })();
  }, [selectedUser]);

  const setR = (k, v) => setRelease(p => ({ ...p, [k]: v }));
  const updTrack = (i, k, v) => setTracks(p => p.map((t, j) => j === i ? { ...t, [k]: v } : t));

  const handleCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const { data } = await uploadFile(`/uploads/artwork?for_user=${selectedUser}`, f, 'artwork');
      setR('cover_art_path', data.file.filename); setCoverPreview(URL.createObjectURL(f)); toast.success('Copertina caricata!');
    } catch (err) { toast.error(err.response?.data?.error || 'Errore upload'); }
  };

  const handleAudio = async (idx, file) => {
    updTrack(idx, '_uploading', true);
    try {
      const { data } = await uploadFile(`/uploads/audio?for_user=${selectedUser}`, file, 'audio', p => updTrack(idx, '_progress', p));
      updTrack(idx, 'audio_path', data.file.filename); updTrack(idx, 'audio_format', data.file.format);
      updTrack(idx, 'audio_duration_seconds', data.file.duration); updTrack(idx, 'audio_sample_rate', data.file.sampleRate);
      updTrack(idx, 'audio_bit_depth', data.file.bitDepth); updTrack(idx, '_uploading', false);
      toast.success(`${file.name} caricato`);
    } catch (err) { updTrack(idx, '_uploading', false); toast.error(err.response?.data?.error || 'Errore'); }
  };

  const addTrack = () => {
    const max = { single: 3, ep: 6, album: 30 }[release.release_type] || 30;
    if (tracks.length >= max) return toast.error(`Max ${max}`);
    setTracks(p => [...p, makeTrack(p.length + 1)]);
  };
  const rmTrack = (i) => { if (tracks.length <= 1) return; setTracks(p => p.filter((_, j) => j !== i).map((t, j) => ({ ...t, track_number: j + 1 }))); };

  // Callback quando un nuovo artista viene creato dal modal
  const handleArtistCreated = (newArtist) => {
    setUserArtists(prev => [...prev, newArtist]);
    const { trackIdx, artistIdx } = artistModal;
    if (trackIdx === -1) {
      // Artista per la release
      if (artistIdx !== null && artistIdx !== undefined) {
        setReleaseArtists(prev => prev.map((a, i) => i === artistIdx ? { ...a, name: newArtist.name, artist_id: newArtist.id } : a));
      } else {
        setReleaseArtists(prev => [...prev, { name: newArtist.name, role: 'primary', artist_id: newArtist.id }]);
      }
    } else if (trackIdx !== null && artistIdx !== null) {
      // Artista per traccia
      setTracks(prev => prev.map((t, ti) => {
        if (ti !== trackIdx) return t;
        const artists = [...(t.artists || [])];
        artists[artistIdx] = { ...artists[artistIdx], name: newArtist.name, artist_id: newArtist.id };
        return { ...t, artists };
      }));
    }
  };

  const saveRelease = async (distribute) => {
    if (!selectedUser) return toast.error('Seleziona un utente');
    if (!release.title) return toast.error('Inserisci il titolo');
    setSaving(true);
    try {
      let rid = releaseId;

      // Crea o aggiorna release
      if (!rid) {
        const { data: rData } = await api.post('/admin/releases/create', { ...release, user_id: selectedUser, artists: releaseArtists });
        rid = rData.release.id;
        setReleaseId(rid);
      } else {
        await api.put(`/admin/releases/${rid}`, { ...release, artists: releaseArtists });
      }

      // Crea o aggiorna tracce
      for (let i = 0; i < tracks.length; i++) {
        const t = tracks[i];
        const td = { ...t }; ['_id', '_expanded', '_uploading', '_progress'].forEach(k => delete td[k]);
        if (td.explicit_content === true) td.explicit_content = 1; else if (td.explicit_content === false) td.explicit_content = 0;
        td.track_number = i + 1;

        try {
          if (t.id) {
            // Traccia già esistente → aggiorna
            await api.put(`/admin/releases/${rid}/tracks/${t.id}`, td);
          } else {
            // Nuova traccia → crea
            const { data: tData } = await api.post(`/admin/releases/${rid}/tracks`, td);
            if (tData.track?.id) {
              setTracks(prev => prev.map((tr, j) => j === i ? { ...tr, id: tData.track.id } : tr));
            }
          }
        } catch (tErr) {
          console.error(`Errore traccia ${i + 1}:`, tErr.response?.data || tErr.message);
        }
      }

      if (distribute) {
        await api.post(`/admin/releases/${rid}/submit-admin`);
        toast.success('Release creata e distribuita!');
        navigate('/admin/releases');
      } else {
        toast.success('Bozza salvata!');
      }
    } catch (e) { toast.error(e.response?.data?.details || e.response?.data?.error || 'Errore creazione'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"/></div>;

  return (
    <div>
      {artistModal.open && (
        <AdminArtistModal
          userId={selectedUser}
          onCreated={handleArtistCreated}
          onClose={() => setArtistModal({ open: false, trackIdx: null, artistIdx: null })}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/releases')} className="p-2 hover:bg-gray-800 rounded-lg text-white"><ArrowLeft size={20}/></button>
          <h1 className="text-xl font-bold text-white">Carica Release per Utente</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => saveRelease(false)} disabled={saving}
            className="px-5 py-2.5 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-500 inline-flex items-center gap-2 disabled:opacity-50">
            <Save size={16}/>{saving ? '...' : 'Salva Bozza'}
          </button>
          <button onClick={() => saveRelease(true)} disabled={saving}
            className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 inline-flex items-center gap-2 disabled:opacity-50">
            <Send size={16}/>{saving ? '...' : 'Salva e Distribuisci'}
          </button>
        </div>
      </div>

      {/* Selezione utente */}
      <div className="card-dark p-4 mb-6">
        <label className="block text-sm font-medium text-gray-300 mb-2">Utente *</label>
        <select className="input-field-dark" value={selectedUser} onChange={e => setSelectedUser(e.target.value)}>
          <option value="">Seleziona l'utente...</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>)}
        </select>
        {selectedUser && userArtists.length > 0 && (
          <p className="text-xs text-gray-500 mt-2">
            {userArtists.length} artisti disponibili per questo utente
          </p>
        )}
        {selectedUser && userArtists.length === 0 && (
          <p className="text-xs text-yellow-600 mt-2">
            Questo utente non ha ancora artisti registrati — puoi crearne di nuovi nelle sezioni tracce qui sotto.
          </p>
        )}
      </div>

      {selectedUser && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Info release */}
            <div className="card-dark p-6">
              <h2 className="font-bold text-white mb-4 flex items-center gap-2"><Music size={18} className="text-yellow-400"/>Informazioni Release</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><label className="block text-xs font-medium text-gray-300 mb-1">Titolo Release *</label><input className="input-field-dark" value={release.title} onChange={e => setR('title', e.target.value)}/></div>
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-gray-300">Artista/i Release *</label>
                    <button onClick={() => setArtistModal({ open: true, trackIdx: -1, artistIdx: null })}
                      className="text-[10px] text-yellow-500 hover:underline flex items-center gap-1">
                      <UserPlus size={9}/> Crea nuovo artista
                    </button>
                  </div>
                  {releaseArtists.map((a, ai) => (
                    <ArtistRowCreate key={ai} idx={ai} artist={a}
                      userArtists={userArtists}
                      onChange={updated => {
                        const ar = [...releaseArtists]; ar[ai] = updated; setReleaseArtists(ar);
                      }}
                      onRemove={() => setReleaseArtists(releaseArtists.filter((_, i) => i !== ai))}
                      canRemove={releaseArtists.length > 1}
                      onCreateNew={() => setArtistModal({ open: true, trackIdx: -1, artistIdx: ai })}
                    />
                  ))}
                  <button onClick={() => setReleaseArtists(p => [...p, { name: '', role: 'featuring', artist_id: null }])}
                    className="text-xs text-yellow-400 font-medium hover:underline">+ Aggiungi Artista</button>
                  {!selectedUser && <p className="text-[10px] text-gray-500 mt-1">Seleziona prima un utente</p>}
                </div>
                <div><label className="block text-xs font-medium text-gray-300 mb-1">Tipo *</label><select className="input-field-dark" value={release.release_type} onChange={e => setR('release_type', e.target.value)}>{RELEASE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-gray-300 mb-1">Label *</label><select className="input-field-dark" value={release.label_id} onChange={e => setR('label_id', e.target.value)}><option value="">Seleziona...</option>{labels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-gray-300 mb-1">Data di Uscita *</label><input type="date" className="input-field-dark" value={release.release_date} onChange={e => setR('release_date', e.target.value)}/></div>
                <div><label className="block text-xs font-medium text-gray-300 mb-1">UPC (opz.)</label><input className="input-field-dark" value={release.upc} onChange={e => setR('upc', e.target.value)}/></div>
                <div><label className="block text-xs font-medium text-gray-300 mb-1">℗ Copyright Fonografico *</label><input className="input-field-dark" value={release.copyright_phonographic} onChange={e => setR('copyright_phonographic', e.target.value)} placeholder="2026 Nome Label"/></div>
                <div><label className="block text-xs font-medium text-gray-300 mb-1">Anno ℗</label><input className="input-field-dark" value={release.copyright_phonographic_year} onChange={e => setR('copyright_phonographic_year', e.target.value)} placeholder="2026"/></div>
              </div>
              <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t border-gray-700">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-300"><input type="checkbox" checked={release.content_id_youtube} onChange={e => setR('content_id_youtube', e.target.checked)}/>Content ID YouTube</label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-300"><input type="checkbox" checked={release.previously_distributed} onChange={e => setR('previously_distributed', e.target.checked)}/>Già distribuito</label>
              </div>
              {release.previously_distributed && <div className="mt-3"><label className="block text-xs text-gray-300 mb-1">Data Pubblicazione Originale</label><input type="date" className="input-field-dark w-48" value={release.original_release_date} onChange={e => setR('original_release_date', e.target.value)}/></div>}
              <div className="mt-4 pt-4 border-t border-gray-700">
                <label className="block text-xs font-medium text-gray-300 mb-2">Copertina *</label>
                <div className="flex items-start gap-4">
                  <div className="w-28 h-28 rounded-lg bg-gray-700 border-2 border-dashed border-gray-600 overflow-hidden flex-shrink-0">
                    {coverPreview ? <img src={coverPreview} alt="" className="w-full h-full object-cover"/> : <div className="flex flex-col items-center justify-center h-full text-gray-500"><Image size={24}/><span className="text-[10px] mt-1">3000×3000</span></div>}
                  </div>
                  <div><input type="file" accept="image/jpeg,image/png" onChange={handleCover} className="hidden" id="admin-cover"/><label htmlFor="admin-cover" className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg cursor-pointer hover:bg-gray-600 inline-flex items-center gap-2 text-sm"><Upload size={16}/>Carica</label></div>
                </div>
              </div>
            </div>

            {/* Tracce */}
            {tracks.map((track, idx) => (
              <div key={track._id} className="card-dark overflow-hidden">
                <div className="flex items-center gap-3 p-4 bg-gray-900 border-b border-gray-700 cursor-pointer"
                  onClick={() => updTrack(idx, '_expanded', !track._expanded)}>
                  <GripVertical size={16} className="text-gray-600"/>
                  <span className="w-7 h-7 rounded-full bg-yellow-500 text-black text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                  <span className="font-medium text-sm text-white flex-1 truncate">{track.title || `Traccia ${idx + 1}`}</span>
                  {track.audio_path && <Check size={16} className="text-green-500"/>}
                  {tracks.length > 1 && <button onClick={e => { e.stopPropagation(); rmTrack(idx); }} className="p-1 text-gray-500 hover:text-red-500"><Trash2 size={16}/></button>}
                  {track._expanded ? <ChevronUp size={16} className="text-gray-500"/> : <ChevronDown size={16} className="text-gray-500"/>}
                </div>

                {track._expanded && (
                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="block text-xs font-medium text-gray-300 mb-1">Titolo *</label><input className="input-field-dark" value={track.title} onChange={e => updTrack(idx, 'title', e.target.value)}/></div>
                      <div><label className="block text-xs font-medium text-gray-300 mb-1">Versione</label><input className="input-field-dark" value={track.version_label || ''} onChange={e => updTrack(idx, 'version_label', e.target.value)} placeholder="Remix, Acoustic..."/></div>
                    </div>
                    <div><label className="block text-xs font-medium text-gray-300 mb-1">Tipo Lavoro *</label><select className="input-field-dark w-64" value={track.work_type || ''} onChange={e => updTrack(idx, 'work_type', e.target.value)}>{WORK_TYPES.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}</select></div>

                    {/* Audio */}
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">File Audio * (WAV/FLAC)</label>
                      {track.audio_path
                        ? <div>
                            <div className="flex items-center gap-3 p-3 bg-green-900/30 rounded-lg border border-green-800">
                              <Check size={16} className="text-green-400"/>
                              <span className="text-sm text-green-300 flex-1">
                                {track.audio_format?.toUpperCase()} · {track.audio_sample_rate}Hz · {track.audio_bit_depth}bit · {Math.floor(track.audio_duration_seconds / 60)}:{String(track.audio_duration_seconds % 60).padStart(2, '0')}
                              </span>
                              <button onClick={() => { updTrack(idx, 'audio_path', ''); updTrack(idx, 'audio_format', ''); }}
                                className="text-gray-500 hover:text-red-400" title="Rimuovi audio">
                                <X size={16}/>
                              </button>
                            </div>
                            <audio controls className="w-full mt-2 h-8" src={`/uploads/audio/${selectedUser}/${track.audio_path}`} preload="none"/>
                          </div>
                        : <div>
                            <input type="file" accept=".wav,.flac" onChange={e => e.target.files[0] && handleAudio(idx, e.target.files[0])} className="hidden" id={`adau-${idx}`}/>
                            <label htmlFor={`adau-${idx}`} className="flex flex-col items-center p-6 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-yellow-500 transition">
                              {track._uploading
                                ? <div className="w-full"><div className="h-2 bg-gray-700 rounded-full"><div className="h-2 bg-yellow-500 rounded-full" style={{ width: `${track._progress}%` }}/></div><p className="text-xs text-center mt-2 text-gray-400">{track._progress}%</p></div>
                                : <><Upload size={20} className="text-gray-500 mb-1"/><span className="text-xs text-gray-500">WAV/FLAC</span></>}
                            </label>
                          </div>
                      }
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div><label className="block text-xs font-medium text-gray-300 mb-1">Lingua *</label><select className="input-field-dark" value={track.language || ''} onChange={e => updTrack(idx, 'language', e.target.value)}><option value="">Seleziona...</option>{LANGUAGES.map(l => <option key={l}>{l}</option>)}</select></div>
                      <div><label className="block text-xs font-medium text-gray-300 mb-1">Genere 1 *</label><select className="input-field-dark" value={track.genre1_id || ''} onChange={e => updTrack(idx, 'genre1_id', e.target.value)}><option value="">-</option>{genres.map(g => <optgroup key={g.id} label={g.name}>{g.subgenres?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</optgroup>)}</select></div>
                      <div><label className="block text-xs font-medium text-gray-300 mb-1">Genere 2</label><select className="input-field-dark" value={track.genre2_id || ''} onChange={e => updTrack(idx, 'genre2_id', e.target.value)}><option value="">-</option>{genres.map(g => <optgroup key={g.id} label={g.name}>{g.subgenres?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</optgroup>)}</select></div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">Contenuto Esplicito *</label>
                        <div className="flex gap-3">
                          <label className={`cursor-pointer px-4 py-2 rounded-lg border-2 text-xs ${track.explicit_content === true ? 'border-red-500 bg-red-900/30 font-bold text-red-300' : 'border-gray-600 text-gray-400 hover:border-gray-500'}`}>
                            <input type="radio" name={`adex${idx}`} checked={track.explicit_content === true} onChange={() => updTrack(idx, 'explicit_content', true)} className="hidden"/>Esplicito
                          </label>
                          <label className={`cursor-pointer px-4 py-2 rounded-lg border-2 text-xs ${track.explicit_content === false ? 'border-green-500 bg-green-900/30 font-bold text-green-300' : 'border-gray-600 text-gray-400 hover:border-gray-500'}`}>
                            <input type="radio" name={`adex${idx}`} checked={track.explicit_content === false} onChange={() => updTrack(idx, 'explicit_content', false)} className="hidden"/>Non Esplicito
                          </label>
                        </div>
                      </div>
                      <div><label className="block text-xs font-medium text-gray-300 mb-1">ISRC (opz.)</label><input className="input-field-dark" value={track.isrc || ''} onChange={e => updTrack(idx, 'isrc', e.target.value.toUpperCase())} maxLength={12}/></div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="block text-xs font-medium text-gray-300 mb-1">© Copyright Traccia</label><input className="input-field-dark" value={track.copyright_text || ''} onChange={e => updTrack(idx, 'copyright_text', e.target.value)} placeholder="2026 Nome Artista"/></div>
                      <div><label className="block text-xs font-medium text-gray-300 mb-1">Anno © Traccia</label><input className="input-field-dark" value={track.copyright_year || ''} onChange={e => updTrack(idx, 'copyright_year', e.target.value)} placeholder="2026"/></div>
                    </div>

                    {track.audio_path && track.audio_duration_seconds > 15 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">Anteprima (inizio)</label>
                        <div className="flex items-center gap-3">
                          <input type="range" min={0} max={Math.max(track.audio_duration_seconds - 15, 0)}
                            value={track.preview_start_seconds || 0}
                            onChange={e => updTrack(idx, 'preview_start_seconds', parseInt(e.target.value))}
                            className="flex-1 accent-yellow-500"/>
                          <span className="text-xs text-gray-400 w-12">{Math.floor((track.preview_start_seconds || 0) / 60)}:{String((track.preview_start_seconds || 0) % 60).padStart(2, '0')}</span>
                        </div>
                      </div>
                    )}

                    {/* Artisti con autocomplete */}
                    <div className="border-t border-gray-700 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-bold text-gray-500 uppercase">Artisti</h4>
                        <button
                          onClick={() => setArtistModal({ open: true, trackIdx: idx, artistIdx: null })}
                          className="text-[10px] text-yellow-500 hover:underline flex items-center gap-1">
                          <UserPlus size={9}/> Crea nuovo artista
                        </button>
                      </div>
                      {(track.artists || []).map((a, ai) => (
                        <ArtistRowCreate key={ai} idx={ai} artist={a}
                          userArtists={userArtists}
                          onChange={updated => {
                            const ar = [...(track.artists || [])]; ar[ai] = updated; updTrack(idx, 'artists', ar);
                          }}
                          onRemove={() => updTrack(idx, 'artists', track.artists.filter((_, i) => i !== ai))}
                          canRemove={(track.artists || []).length > 1}
                          onCreateNew={() => setArtistModal({ open: true, trackIdx: idx, artistIdx: ai })}
                        />
                      ))}
                      <button
                        onClick={() => updTrack(idx, 'artists', [...(track.artists || []), { name: '', role: 'featuring', artist_id: null }])}
                        className="text-xs text-yellow-400 font-medium hover:underline">
                        + Aggiungi Artista
                      </button>
                    </div>

                    {/* Contributori */}
                    <div className="border-t border-gray-700 pt-4">
                      <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Contributori / Credits</h4>
                      {(track.contributors || []).map((c, ci) => (
                        <div key={ci} className="flex items-center gap-2 mb-2">
                          <select className="input-field-dark w-44 text-xs" value={c.role}
                            onChange={e => { const cr = [...track.contributors]; cr[ci] = { ...c, role: e.target.value }; updTrack(idx, 'contributors', cr); }}>
                            {CONTRIBUTOR_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                          <input className="input-field-dark flex-1" placeholder="Nome..." value={c.name || ''}
                            onChange={e => { const cr = [...track.contributors]; cr[ci] = { ...c, name: e.target.value }; updTrack(idx, 'contributors', cr); }}/>
                          {track.contributors.length > 1 && (
                            <button onClick={() => updTrack(idx, 'contributors', track.contributors.filter((_, i) => i !== ci))} className="p-1 text-gray-500 hover:text-red-400"><X size={14}/></button>
                          )}
                        </div>
                      ))}
                      <button onClick={() => updTrack(idx, 'contributors', [...(track.contributors || []), { name: '', role: 'arranger' }])}
                        className="text-xs text-yellow-400 font-medium hover:underline">+ Aggiungi Contributore
                      </button>
                    </div>

                    <div className="border-t border-gray-700 pt-4 flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-300"><input type="checkbox" checked={!!track.ai_generated} onChange={e => updTrack(idx, 'ai_generated', e.target.checked)}/>Creata con AI</label>
                      <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-300"><input type="checkbox" checked={!!track.instrumental} onChange={e => updTrack(idx, 'instrumental', e.target.checked)}/>Strumentale</label>
                    </div>

                    <div className="border-t border-gray-700 pt-4">
                      <label className="block text-xs font-medium text-gray-300 mb-1">Testo / Lyrics</label>
                      <textarea className="input-field-dark h-20 resize-y" value={track.lyrics || ''} onChange={e => updTrack(idx, 'lyrics', e.target.value)}/>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <button onClick={addTrack}
              className="w-full py-4 border-2 border-dashed border-gray-700 rounded-xl text-gray-500 hover:text-yellow-400 hover:border-yellow-500 transition flex items-center justify-center gap-2">
              <Plus size={18}/>Aggiungi Traccia
            </button>
          </div>

          {/* Sidebar anteprima */}
          <div className="hidden lg:block">
            <div className="card-dark p-5 sticky top-8">
              <h3 className="text-xs font-bold text-gray-500 uppercase mb-4">Anteprima</h3>
              <div className="w-full aspect-square rounded-lg bg-gray-700 overflow-hidden mb-4">
                {coverPreview ? <img src={coverPreview} alt="" className="w-full h-full object-cover"/> : <div className="flex items-center justify-center h-full text-gray-600"><Image size={48}/></div>}
              </div>
              <h2 className="font-bold text-white text-lg">{release.title || 'Titolo Release'}</h2>
              <p className="text-sm text-gray-400 mt-1">{releaseArtists.filter(a=>a.name).map(a=>a.name).join(', ') || 'Artista'}</p>
              <div className="mt-4 space-y-2 text-xs text-gray-500">
                <div className="flex justify-between"><span>Artista:</span><span className="text-yellow-400 font-medium">{releaseArtists.filter(a=>a.name).map(a=>a.name).join(', ') || '-'}</span></div>
                <div className="flex justify-between"><span>Tipo:</span><span className="text-white">{RELEASE_TYPES.find(t => t.value === release.release_type)?.label}</span></div>
                <div className="flex justify-between"><span>Label:</span><span className="text-white">{labels.find(l => String(l.id) === String(release.label_id))?.name || '-'}</span></div>
                <div className="flex justify-between"><span>Tracce:</span><span className="text-white">{tracks.length}</span></div>
                <div className="flex justify-between"><span>Data:</span><span className="text-white">{release.release_date || '-'}</span></div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Tracce</p>
                {tracks.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs mb-1">
                    <span className="w-5 text-gray-600">{i + 1}.</span>
                    <span className={`flex-1 truncate ${t.audio_path ? 'text-white' : 'text-gray-600'}`}>{t.title || `Track ${i + 1}`}</span>
                    {t.audio_path && <Check size={12} className="text-green-500"/>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
